import { notEmpty, scmpPlus } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, useFragment, useQueryLoader } from "react-relay";

import { article, data } from "shared/data";

import type { morningStudioArticleFooterContent$key } from "scmp-app/queries/__generated__/morningStudioArticleFooterContent.graphql";
import type { morningStudioArticleFooterQuery } from "scmp-app/queries/__generated__/morningStudioArticleFooterQuery.graphql";

import { MorningStudioArticleFooterDisplay } from "./morning-studio-article-footer-display";

export type Props = {
  className?: string;
  reference: morningStudioArticleFooterContent$key;
};

export const query = graphql`
  query morningStudioArticleFooterQuery(
    $applicationId: String!
    $articleTypeId: String!
    $entityUuid: String!
    $scmpPlusPaywallTypeIds: [String]
    $topicEntityUuid: String
    $withTopic: Boolean!
  ) {
    ...morningStudioArticleFooterDisplayQuery
      @arguments(
        applicationId: $applicationId
        articleTypeId: $articleTypeId
        entityUuid: $entityUuid
        scmpPlusPaywallTypeIds: $scmpPlusPaywallTypeIds
        topicEntityUuid: $topicEntityUuid
        withTopic: $withTopic
      )
  }
`;

export const MorningStudioArticleFooter: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const content = useFragment(
    graphql`
      fragment morningStudioArticleFooterContent on Content {
        entityUuid
        topics {
          entityUuid
        }
      }
    `,
    reference_,
  );
  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<morningStudioArticleFooterQuery>(query);

  useEffect(() => {
    if (!content.entityUuid) return;

    const firstTopic = first(content.topics);

    loadQuery({
      applicationId: data.application.scmp.entityUuid,
      articleTypeId: article.types.studio.entityId,
      entityUuid: content.entityUuid,
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
      topicEntityUuid: firstTopic?.entityUuid,
      withTopic: notEmpty(firstTopic),
    });

    return disposeQuery;
  }, [content?.entityUuid, content?.topics, disposeQuery, loadQuery]);

  return queryReference ? (
    <MorningStudioArticleFooterDisplay className={className} queryReference={queryReference} />
  ) : null;
};

MorningStudioArticleFooter.displayName = "MorningStudioArticleFooter";
